import { lazy, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  NavLinkStyled,
  NavButtonStyled,
  NavArrowIndicator,
} from '../navigation.style';
import * as Styled from './mega-nav.style';
import {
  NAV_BENEFITS,
  NAV_BENEFITS_LABEL,
  NAV_LESSONS,
  NAV_LESSONS_LABEL,
  NAV_CONTACT_US,
  NAV_PRICING,
  NAV_FOR_TEACHERS_LABEL,
  NAV_FOR_SCHOOLS_LABEL,
  NAV_STIMULUS_LIBRARY_LABEL,
  NAV_SCOPE_AND_SEQUENCE_LABEL,
  NAV_PRICING_LABEL,
  NAV_CONTACT_US_LABEL,
  Props,
  NAV_YEARS,
} from './mega-nav.constants';
import {
  PUBLIC_CONTACT_US,
  PUBLIC_PRICING,
  PUBLIC_SCOPE_AND_SEQUENCE,
  PUBLIC_STIMULUS,
  PUBLIC_SCHOOL_BENEFITS,
  PUBLIC_TEACHER_BENEFITS,
  PUBLIC_LESSONS,
} from 'global/public-routes.constants';
import {
  isLessonActive,
  isScopeAndSequenceActive,
  isBenefitsActive,
  isContactUsActive,
  pushNavOpenDataEvent,
  pushNavFollowDataEvent,
  pushNavSublinkDataEvent,
} from '../navigation.utils';
import { setContentFilterData } from 'approot/shared/filters/filters.apollo';
import { useLayoutLinks } from '../../layout.hooks';
import { useGetStartupData } from 'approot/shared/graphql/startup-data/startup-data.hooks';
import { useYearTranslation } from 'approot/shared/logic/years';
import { useRegionData } from 'approot/shared/region/region.apollo';
import ImageContentPlay from 'approot/shared/images/content/image-content-play';
import ImageContentBook from 'approot/shared/images/content/image-content-book';
import ImageContentTeacher from 'approot/shared/images/content/image-content-teacher';
import ImageContentSchool from 'approot/shared/images/content/image-content-school';

const MegaSubject = lazy(() => import('./mega-subjects'));
const MegaDiscipline = lazy(() => import('./mega-disciplines'));

const MegaNavUnauthenticated = ({
  activeId,
  handleOnClick,
  handleOnFocus,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { isRegionAU } = useRegionData();
  const { getYearLabel } = useYearTranslation();
  const { pathname } = useLocation();

  const { data, disciplines, yearOptions } = useGetStartupData();
  const subjects = data?.startupData.subjects;

  const yearLabel = useMemo(() => {
    return getYearLabel({ plural: false });
  }, [getYearLabel]);

  const yearsLabel = useMemo(() => {
    return getYearLabel({ plural: true });
  }, [getYearLabel]);

  const totalSubjects = useMemo(() => {
    return disciplines && disciplines.length > 0
      ? disciplines.length
      : (subjects || []).length;
  }, [disciplines, subjects]);

  const totalYears = useMemo(() => {
    return (yearOptions || []).length;
  }, [yearOptions]);

  const { goToScopeAndSequence } = useLayoutLinks();

  const handleNavClick = useCallback(
    (isExpanded: boolean, label: string) => {
      if (isExpanded) {
        pushNavOpenDataEvent(label);
      }
      handleOnClick(label);
    },
    [handleOnClick]
  );

  const handleLinkClick = useCallback(
    (menu: string, label: string) => {
      pushNavSublinkDataEvent(menu, label);
      handleOnClick('');
    },
    [handleOnClick]
  );

  return (
    <Styled.MegaNavMenuGroup>
      <Styled.MegaNavMenuItem $isActive={isBenefitsActive(pathname)}>
        <NavButtonStyled
          type="button"
          id={NAV_BENEFITS}
          isFocussed={activeId === NAV_BENEFITS}
          aria-haspopup
          aria-expanded={activeId === NAV_BENEFITS}
          onClick={() =>
            handleNavClick(activeId !== NAV_BENEFITS, NAV_BENEFITS)
          }
          onFocus={handleOnFocus}
        >
          <Styled.MegaNavButtonText
            data-title={NAV_BENEFITS_LABEL}
            data-selector="header-menu-benefits"
          >
            {NAV_BENEFITS_LABEL}
          </Styled.MegaNavButtonText>
          <NavArrowIndicator width={14} height={12} />
        </NavButtonStyled>
        <Styled.MegaNavMenu>
          <Styled.MegaNavLink
            to={PUBLIC_TEACHER_BENEFITS}
            id={`${NAV_BENEFITS}-1`}
            data-selector="nav-menu-for-teachers"
            onClick={() =>
              handleLinkClick(NAV_BENEFITS, NAV_FOR_TEACHERS_LABEL)
            }
          >
            <Styled.MegaNavIcon>
              <ImageContentTeacher width={48} />
            </Styled.MegaNavIcon>
            <span>{NAV_FOR_TEACHERS_LABEL}</span>
          </Styled.MegaNavLink>
          <Styled.MegaNavLink
            to={PUBLIC_SCHOOL_BENEFITS}
            id={`${NAV_BENEFITS}-2`}
            data-selector="nav-menu-for-schools"
            onClick={() => handleLinkClick(NAV_BENEFITS, NAV_FOR_SCHOOLS_LABEL)}
          >
            <Styled.MegaNavIcon>
              <ImageContentSchool width={48} />
            </Styled.MegaNavIcon>
            <span>{NAV_FOR_SCHOOLS_LABEL}</span>
          </Styled.MegaNavLink>
        </Styled.MegaNavMenu>
      </Styled.MegaNavMenuItem>
      <Styled.MegaNavMenuItem
        $isActive={
          isLessonActive(pathname) || isScopeAndSequenceActive(pathname)
        }
      >
        <NavButtonStyled
          type="button"
          id={NAV_LESSONS}
          isFocussed={activeId === NAV_LESSONS}
          aria-haspopup
          aria-expanded={activeId === NAV_LESSONS}
          onClick={() => handleNavClick(activeId !== NAV_LESSONS, NAV_LESSONS)}
          onFocus={handleOnFocus}
        >
          <Styled.MegaNavButtonText
            data-title={NAV_LESSONS_LABEL}
            data-selector="header-menu-lessons"
          >
            {NAV_LESSONS_LABEL}
          </Styled.MegaNavButtonText>
          <NavArrowIndicator width={14} height={12} />
        </NavButtonStyled>
        <Styled.MegaNavMenu>
          <Styled.MegaNavColumns>
            <Styled.MegaNavColumn>
              <Styled.MegaNavSubTitle>
                BROWSE BY {disciplines ? 'DISCIPLINE' : 'SUBJECT'}
              </Styled.MegaNavSubTitle>
              <Styled.MegaNavStackedList>
                {disciplines
                  ? disciplines.map((discipline, index) => (
                      <MegaDiscipline
                        key={discipline.id}
                        discipline={discipline}
                        index={index}
                        onClick={() => {
                          setContentFilterData({
                            triggerContentRefresh: true,
                          });
                          handleLinkClick(NAV_LESSONS, discipline.label);
                        }}
                      />
                    ))
                  : subjects?.map((subject, index) => (
                      <MegaSubject
                        key={subject.name}
                        subject={subject}
                        index={index}
                        onClick={() => {
                          setContentFilterData({
                            triggerContentRefresh: true,
                          });
                          handleLinkClick(NAV_LESSONS, subject.label);
                        }}
                      />
                    ))}
              </Styled.MegaNavStackedList>
            </Styled.MegaNavColumn>
            <Styled.MegaNavColumn>
              <Styled.MegaNavSubTitle>
                BROWSE BY {yearLabel.toUpperCase()}
              </Styled.MegaNavSubTitle>
              <Styled.MegaNavStackedList>
                {yearOptions?.map((year, index) => (
                  <li key={year.value}>
                    <Styled.MegaNavSmallLink
                      to={`${PUBLIC_LESSONS}?years=${year.value}${
                        isRegionAU ? '&view=units' : ''
                      }`}
                      key={year.value}
                      id={`${NAV_LESSONS}-${totalSubjects + 1 + index}`}
                      aria-label={`Browse by ${year.label}`}
                      onClick={e => {
                        e.preventDefault();

                        setContentFilterData({
                          yearFilter: [year.value],
                          curriculumFilter: [],
                          subjectFilter: [],
                          curriculumVersionFilter: [],
                          triggerContentRefresh: true,
                        });
                        navigate(
                          `${PUBLIC_LESSONS}?years=${year.value}${
                            isRegionAU ? '&view=units' : ''
                          }`
                        );
                        handleLinkClick(NAV_YEARS, year.label);
                      }}
                    >
                      {year.label}
                    </Styled.MegaNavSmallLink>
                  </li>
                ))}
                <li>
                  <Styled.MegaNavSmallLink
                    to={`${PUBLIC_LESSONS}${isRegionAU ? '?view=units' : ''}`}
                    id={`${NAV_LESSONS}-${totalYears + totalSubjects + 1}`}
                    aria-label={`Browse All ${yearsLabel}`}
                    onClick={e => {
                      e.preventDefault();

                      setContentFilterData({
                        yearFilter: [],
                        curriculumFilter: [],
                        subjectFilter: [],
                        curriculumVersionFilter: [],
                        triggerContentRefresh: true,
                      });
                      handleLinkClick(NAV_YEARS, `All ${yearsLabel}`);

                      navigate(
                        `${PUBLIC_LESSONS}${isRegionAU ? '?view=units' : ''}`
                      );
                    }}
                  >
                    All {yearsLabel}
                  </Styled.MegaNavSmallLink>
                </li>
              </Styled.MegaNavStackedList>
            </Styled.MegaNavColumn>
          </Styled.MegaNavColumns>
          <Styled.MegaNavHr />
          <Styled.MegaNavList>
            <li>
              <Styled.MegaNavLink
                to={PUBLIC_STIMULUS}
                id={`${NAV_LESSONS}-${totalYears + totalSubjects + 2}`}
                data-selector="nav-menu-all-stimulus"
                onClick={() =>
                  handleLinkClick(NAV_LESSONS, NAV_STIMULUS_LIBRARY_LABEL)
                }
              >
                <Styled.MegaNavIcon>
                  <ImageContentPlay width={43} />
                </Styled.MegaNavIcon>
                <div>
                  <Styled.MegaNavText>
                    {NAV_STIMULUS_LIBRARY_LABEL}
                  </Styled.MegaNavText>
                  <Styled.MegaNavSmallText>
                    Browse our eBooks, interactives, videos, and thousands more
                    quality stimulus.
                  </Styled.MegaNavSmallText>
                </div>
              </Styled.MegaNavLink>
            </li>
            <li>
              <Styled.MegaNavLink
                to={PUBLIC_SCOPE_AND_SEQUENCE}
                id={`${NAV_LESSONS}-${totalYears + totalSubjects + 3}`}
                data-selector="nav-menu-scope-and-sequence"
                onClick={e => {
                  goToScopeAndSequence(e);
                  handleLinkClick(NAV_LESSONS, NAV_SCOPE_AND_SEQUENCE_LABEL);
                }}
              >
                <Styled.MegaNavIcon>
                  <ImageContentBook width={43} />
                </Styled.MegaNavIcon>
                <div>
                  <Styled.MegaNavText>
                    {NAV_SCOPE_AND_SEQUENCE_LABEL}
                  </Styled.MegaNavText>
                  <Styled.MegaNavSmallText>
                    {t('banner.scopeAndSequenceNav')}
                  </Styled.MegaNavSmallText>
                </div>
              </Styled.MegaNavLink>
            </li>
          </Styled.MegaNavList>
        </Styled.MegaNavMenu>
      </Styled.MegaNavMenuItem>
      <Styled.MegaNavMenuItem $isActive={pathname === PUBLIC_PRICING}>
        <NavLinkStyled
          data-title={NAV_PRICING_LABEL}
          data-selector="header-menu-pricing"
          id={NAV_PRICING}
          to={PUBLIC_PRICING}
          onFocus={handleOnFocus}
          onClick={() => {
            if (pathname !== PUBLIC_PRICING) {
              pushNavFollowDataEvent(NAV_PRICING_LABEL);
            }
          }}
        >
          {NAV_PRICING_LABEL}
        </NavLinkStyled>
      </Styled.MegaNavMenuItem>
      <Styled.MegaNavMenuItem $isActive={isContactUsActive(pathname)}>
        <NavLinkStyled
          data-title={NAV_CONTACT_US_LABEL}
          data-selector="nav-menu-contact-us"
          to={PUBLIC_CONTACT_US}
          id={NAV_CONTACT_US}
          onFocus={handleOnFocus}
          onClick={() => {
            if (pathname !== PUBLIC_CONTACT_US) {
              pushNavFollowDataEvent(NAV_CONTACT_US_LABEL);
            }
          }}
        >
          {NAV_CONTACT_US_LABEL}
        </NavLinkStyled>
      </Styled.MegaNavMenuItem>
    </Styled.MegaNavMenuGroup>
  );
};

export default MegaNavUnauthenticated;
